import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import { timestampInSeconds, logger, spanToJSON, uuid4, getCurrentScope } from '@sentry/core';
import { DEBUG_BUILD } from '../debug-build.js';
import { WINDOW } from '../helpers.js';
import { isAutomatedPageLoadSpan, startJSSelfProfile, MAX_PROFILE_DURATION_MS, addProfileToGlobalCache } from './utils.js';

/**
 * Wraps startTransaction and stopTransaction with profiling related logic.
 * startProfileForTransaction is called after the call to startTransaction in order to avoid our own code from
 * being profiled. Because of that same reason, stopProfiling is called before the call to stopTransaction.
 */
function startProfileForSpan(span) {
  // Start the profiler and get the profiler instance.
  var startTimestamp;
  if (isAutomatedPageLoadSpan(span)) {
    startTimestamp = timestampInSeconds() * 1000;
  }
  var profiler = startJSSelfProfile();

  // We failed to construct the profiler, so we skip.
  // No need to log anything as this has already been logged in startProfile.
  if (!profiler) {
    return;
  }
  if (DEBUG_BUILD) {
    logger.log("[Profiling] started profiling span: ".concat(spanToJSON(span).description));
  }

  // We create "unique" span names to avoid concurrent spans with same names
  // from being ignored by the profiler. From here on, only this span name should be used when
  // calling the profiler methods. Note: we log the original name to the user to avoid confusion.
  var profileId = uuid4();
  getCurrentScope().setContext('profile', {
    profile_id: profileId,
    start_timestamp: startTimestamp
  });

  /**
   * Idempotent handler for profile stop
   */
  function onProfileHandler() {
    return _onProfileHandler.apply(this, arguments);
  } // Enqueue a timeout to prevent profiles from running over max duration.
  function _onProfileHandler() {
    _onProfileHandler = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (span) {
              _context.next = 2;
              break;
            }
            return _context.abrupt("return");
          case 2:
            if (profiler) {
              _context.next = 4;
              break;
            }
            return _context.abrupt("return");
          case 4:
            return _context.abrupt("return", profiler.stop().then(function (profile) {
              if (maxDurationTimeoutID) {
                WINDOW.clearTimeout(maxDurationTimeoutID);
                maxDurationTimeoutID = undefined;
              }
              if (DEBUG_BUILD) {
                logger.log("[Profiling] stopped profiling of span: ".concat(spanToJSON(span).description));
              }

              // In case of an overlapping span, stopProfiling may return null and silently ignore the overlapping profile.
              if (!profile) {
                if (DEBUG_BUILD) {
                  logger.log("[Profiling] profiler returned null profile for: ".concat(spanToJSON(span).description), 'this may indicate an overlapping span or a call to stopProfiling with a profile title that was never started');
                }
                return;
              }
              addProfileToGlobalCache(profileId, profile);
            }).catch(function (error) {
              if (DEBUG_BUILD) {
                logger.log('[Profiling] error while stopping profiler:', error);
              }
            }));
          case 5:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _onProfileHandler.apply(this, arguments);
  }
  var maxDurationTimeoutID = WINDOW.setTimeout(function () {
    if (DEBUG_BUILD) {
      logger.log('[Profiling] max profile duration elapsed, stopping profiling for:', spanToJSON(span).description);
    }
    // If the timeout exceeds, we want to stop profiling, but not finish the span
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    onProfileHandler();
  }, MAX_PROFILE_DURATION_MS);

  // We need to reference the original end call to avoid creating an infinite loop
  var originalEnd = span.end.bind(span);

  /**
   * Wraps span `end()` with profiling related logic.
   * startProfiling is called after the call to spanStart in order to avoid our own code from
   * being profiled. Because of that same reason, stopProfiling is called before the call to spanEnd.
   */
  function profilingWrappedSpanEnd() {
    if (!span) {
      return originalEnd();
    }
    // onProfileHandler should always return the same profile even if this is called multiple times.
    // Always call onProfileHandler to ensure stopProfiling is called and the timeout is cleared.
    void onProfileHandler().then(function () {
      originalEnd();
    }, function () {
      // If onProfileHandler fails, we still want to call the original finish method.
      originalEnd();
    });
    return span;
  }
  span.end = profilingWrappedSpanEnd;
}
export { startProfileForSpan };